import { useState, useEffect, useCallback } from "react";
import { Image, Plus, Trash2, Eye, EyeOff, GripVertical } from "lucide-react";
import AppShell from "../components/layout/AppShell";
import api from "../services/api";
import BannerForm from "../components/banners/BannerForm";

function BannerCard({ banner, imageUrl, onToggle, onDelete }) {
  var activeCls = banner.is_active ? "border-emerald-200" : "border-gray-200 opacity-60";
  return (
    <div className={"bg-white rounded-xl p-3 shadow-sm border flex gap-4 items-center " + activeCls} data-testid={"banner-card-" + banner.id}>
      <div className="w-24 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
        {imageUrl ? <img src={imageUrl} alt={banner.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Image className="w-6 h-6 text-gray-300" /></div>}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[#0A2F3D] truncate">{banner.title}</p>
        {banner.description && <p className="text-xs text-gray-400 truncate">{banner.description}</p>}
        {banner.link_url && <p className="text-[10px] text-[#3B82F6] truncate">{banner.link_url}</p>}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <span className={"px-2 py-0.5 rounded-full text-[10px] font-medium " + (banner.is_active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500")}>{banner.is_active ? "Ativo" : "Inativo"}</span>
        <button onClick={function() { onToggle(banner); }} className="p-1.5 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-[#3B82F6]" data-testid={"toggle-banner-" + banner.id}>
          {banner.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
        <button onClick={function() { onDelete(banner.id); }} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500" data-testid={"delete-banner-" + banner.id}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default function BannersPage() {
  var bannersState = useState([]);
  var banners = bannersState[0];
  var setBanners = bannersState[1];
  var imagesState = useState({});
  var images = imagesState[0];
  var setImages = imagesState[1];
  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  var showFormState = useState(false);
  var showForm = showFormState[0];
  var setShowForm = showFormState[1];

  var fetchBanners = useCallback(function() {
    setLoading(true);
    api.get("/banners").then(function(res) {
      var list = res.data.banners || [];
      setBanners(list);
      list.forEach(function(b) {
        api.get("/banners/" + b.id + "/image", { responseType: "blob" }).then(function(r) {
          var url = URL.createObjectURL(r.data);
          setImages(function(prev) { var n = {}; for (var k in prev) n[k] = prev[k]; n[b.id] = url; return n; });
        }).catch(function() {});
      });
    }).finally(function() { setLoading(false); });
  }, []);

  useEffect(function() { fetchBanners(); }, [fetchBanners]);

  function handleToggle(b) {
    var fd = new FormData();
    fd.append("is_active", b.is_active ? "false" : "true");
    api.put("/banners/" + b.id, fd, { headers: { "Content-Type": "multipart/form-data" } }).then(function() { fetchBanners(); });
  }

  function handleDelete(id) {
    if (!window.confirm("Excluir este banner?")) return;
    api.delete("/banners/" + id).then(function() { fetchBanners(); });
  }

  return (
    <AppShell activePage="banners" title="Banners">
      <div data-testid="banners-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-bold text-[#0A2F3D]">Gerenciar Banners</h2>
            <p className="text-xs text-gray-500">Banners exibidos na area do cliente</p>
          </div>
          <button onClick={function() { setShowForm(true); }} className="flex items-center gap-2 px-4 py-2 bg-[#3B82F6] text-white rounded-lg text-sm font-medium hover:bg-blue-600" data-testid="new-banner-btn">
            <Plus className="w-4 h-4" />Novo Banner
          </button>
        </div>

        {showForm && <BannerForm onSave={function() { setShowForm(false); fetchBanners(); }} onCancel={function() { setShowForm(false); }} />}

        {loading ? (
          <div className="flex justify-center py-16"><div className="w-8 h-8 border-3 border-[#3B82F6] border-t-transparent rounded-full animate-spin" /></div>
        ) : banners.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <Image className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">Nenhum banner cadastrado</p>
          </div>
        ) : (
          <div className="space-y-3">
            {banners.map(function(b) { return <BannerCard key={b.id} banner={b} imageUrl={images[b.id]} onToggle={handleToggle} onDelete={handleDelete} />; })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
